/**
 * Formats numériques de l'écran Abonnements (`06` §8) : montants en euros et
 * compteurs à la française (`29,90 €`, `4 980`). Un seul point de formatage
 * pour les onglets, jamais de `toFixed` ou de `€` concaténé à la main.
 */

const EURO = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
const SIGNED_EURO = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  signDisplay: 'exceptZero',
});
const COUNT = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

/** Nombre fini, ou décimal en chaîne tel que renvoyé par Recharge (`"29.90"`). */
function toAmount(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Vrai signe moins typographique (U+2212) plutôt que le trait d'union d'`Intl`. */
function typographicMinus(formatted: string): string {
  return formatted.replace(/^-/, '\u2212');
}

/**
 * `29,90 €` ; `null` (afficher « — ») pour une valeur absente. Une chaîne
 * illisible est rendue telle quelle suivie de l'euro, pour ne rien masquer.
 */
export function formatEuros(value: number | string | null | undefined): string | null {
  const amount = toAmount(value);
  if (amount === null) {
    return typeof value === 'string' && value.trim() !== '' ? `${value.trim()}\u00A0€` : null;
  }
  return typographicMinus(EURO.format(amount));
}

/** Écart signé : `−0,47 €`, `+1,20 €`, `0,00 €` ; `null` pour une valeur absente. */
export function formatSignedEuros(value: number | null | undefined): string | null {
  const amount = toAmount(value);
  return amount === null ? null : typographicMinus(SIGNED_EURO.format(amount));
}

/** Compteur entier groupé à la française : `4 980`. */
export function formatCount(value: number): string {
  return COUNT.format(value);
}

/** `1 adresse lue`, `4 980 adresses lues` : 0 et 1 au singulier, comme le veut le français. */
export function formatCountLabel(value: number, singular: string, plural: string): string {
  return `${formatCount(value)}\u00A0${Math.abs(value) < 2 ? singular : plural}`;
}
